/**
 * Session Modal Tracker
 *
 * A lightweight module-level tracker for session-critical modals.
 * Used to prevent JWT session expiry from interrupting critical user flows
 * (e.g., eKYC verification, document signing).
 *
 * USAGE:
 * - Modals register themselves when opened via useSessionCriticalModal hook
 * - JWT expiry hook checks hasActiveModals() before logging out on login page
 * - No Redux, no persistence - purely ephemeral runtime state
 *
 * DESIGN DECISIONS:
 * - Module-level singleton: Simple, no context provider overhead
 * - Set-based tracking: O(1) operations, prevents duplicates
 * - String IDs: Allows any modal to register without type constraints
 */

// Set of currently active session-critical modal IDs
const activeModals = new Set<string>();

// Optional: Callback for debugging/monitoring
type ModalChangeCallback = (activeModalIds: string[]) => void;
let onChangeCallback: ModalChangeCallback | null = null;

/**
 * Register a session-critical modal as active.
 * Call this when a modal opens.
 *
 * @param modalId - Unique identifier for the modal (e.g., 'regtank-ekyc', 'docusign-agreement')
 */
export function registerSessionModal(modalId: string): void {
  activeModals.add(modalId);
  onChangeCallback?.(Array.from(activeModals));
}

/**
 * Unregister a session-critical modal.
 * Call this when a modal closes.
 *
 * @param modalId - The same identifier used when registering
 */
export function unregisterSessionModal(modalId: string): void {
  activeModals.delete(modalId);
  onChangeCallback?.(Array.from(activeModals));
}

/**
 * Check if any session-critical modals are currently active.
 * Used by JWT expiry hook to determine if logout should proceed.
 *
 * @returns true if at least one modal is active
 */
export function hasActiveSessionModals(): boolean {
  return activeModals.size > 0;
}

/**
 * Get list of currently active modal IDs.
 * Useful for debugging.
 *
 * @returns Array of active modal IDs
 */
export function getActiveSessionModals(): string[] {
  return Array.from(activeModals);
}

/**
 * Clear all registered modals.
 * Useful for cleanup during logout or testing.
 */
export function clearAllSessionModals(): void {
  activeModals.clear();
  onChangeCallback?.([]);
}

/**
 * Set a callback to be notified of modal state changes.
 * Useful for debugging or monitoring.
 *
 * @param callback - Function called with array of active modal IDs
 */
export function setOnChangeCallback(callback: ModalChangeCallback | null): void {
  onChangeCallback = callback;
}

// Export as named object for cleaner imports
export const sessionModalTracker = {
  register: registerSessionModal,
  unregister: unregisterSessionModal,
  hasActiveModals: hasActiveSessionModals,
  getActiveModals: getActiveSessionModals,
  clearAll: clearAllSessionModals,
  setOnChangeCallback,
};

export default sessionModalTracker;
